// @flow

import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { log, formatPhoneNumberForFrontend } from '../../utils/jsUtils';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

import FileCopy from '@mui/icons-material/FileCopy';
import RemoveIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/StaffListContent.Style';
import MemberStructuralStyles from '../Styles/CommonMemberStructuralStyles.Style';
import commonListTableStyles from '../Styles/CommonListTable.Style';

import ConfigurableTable from '../SharedComponents/ConfigurableTable';
import ContentContainer from '../SharedComponents/ContentContainer';

import AddPropertyAccessDialog from './AddPropertyAccessDialog';
import RemovePropertyAccessDialog from './RemovePropertyAccessDialog';
import { getUserRole, getRolePower, ROLE_POWER } from '../../models/User';
import RemoveStaff from './RemoveStaffDialog';
import EditStaffDialog from './EditStaffDialog';
import SPBackend from '../../services/SPBackend';
import { auth } from '../../services/firebase';

// Actions
import { setSnackMessage, setLoadingModule } from '../../reduxStore/modules/common/actions';
import { setAccountSettings } from '../../reduxStore/modules/user/actions';

// RTK Query
import {
  useGetStaffListQuery,
  useRemoveStaffMutation,
  useEditStaffMutation,
  useUpdateStaffCallForwardMutation
} from '../../features/api/staffApi';

// Selectors
import { selectUserRole, selectUserEmail, selectAccountSettings, selectAccountId } from '../../reduxStore/modules/user/selectors';
import { useI18n } from '../../contexts/I18nContext';

import type { User, UserView } from '../../services/SPBackend';


type Props = {
  classes?: Object
};

// Table configuration for Staff
const STAFF_TABLE_CONFIG = {
  columns: [
    {
      key: 'name',
      headerLabel: 'Full Name',
      width: 350,
      flex: '1 1 auto',
      sortable: false
    },
    {
      key: 'email',
      headerLabel: 'Email',
      width: 350,
      flex: '1 1 auto',
      sortable: false
    },
    {
      key: 'phone',
      headerLabel: 'Phone',
      width: 180,
      flex: '0 0 auto',
      sortable: false
    },
    {
      key: 'role',
      headerLabel: 'Role',
      width: 120,
      flex: '0 0 auto',
      sortable: false
    },
    {
      key: 'callForward',
      headerLabel: 'Call Forward',
      width: 200,
      flex: '0 0 auto',
      sortable: false
    }
  ],
  actions: [
    {
      key: 'edit',
      icon: EditIcon,
      label: 'Edit Staff',
      onClick: 'handleEditStaff'
    },
    {
      key: 'remove',
      icon: RemoveIcon,
      label: 'Remove Staff',
      onClick: 'handleRemoveStaff'
    }
  ],
  enableMultiSelect: false
};

// eslint-disable-next-line react/no-multi-comp
const StaffListContent = (props: Props) => {
  const theme = useTheme();
  const combinedStyles = {
    ...commonListTableStyles,
    ...styles,
    ...MemberStructuralStyles
  };
  const classes = createClasses(combinedStyles, theme);

  const dispatch = useDispatch();
  const userRole = useSelector(selectUserRole);
  const email = useSelector(selectUserEmail);
  const accountSettings = useSelector(selectAccountSettings);
  const accountId = useSelector(selectAccountId);
  const i18n = useI18n();

  // RTK Query for staff data
  const { data: staffList = [], isFetching, refetch } = useGetStaffListQuery();
  const [removeStaffMutation] = useRemoveStaffMutation();
  const [editStaffMutation] = useEditStaffMutation();
  const [updateStaffCallForward] = useUpdateStaffCallForwardMutation();

  // Sync loading state to Redux for subheader spinner
  useEffect(() => {
    dispatch(setLoadingModule('staff', isFetching));
    return () => {
      dispatch(setLoadingModule('staff', false));
    };
  }, [isFetching, dispatch]);

  // Global call forward state
  const isInboundCallsEnabled = accountSettings?.Twilio?.InboundCallsEnabled ?? false;
  const [isTogglingGlobal, setIsTogglingGlobal] = useState(false);

  // Dialog states
  const [openConfirmRemoveDialog, setOpenConfirmRemoveDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [openAddPropertyAccessDialog, setOpenAddPropertyAccessDialog] = useState(false);
  const [openConfirmEditDialog, setOpenConfirmEditDialog] = useState(false);
  const [openRemovePropertyAccessDialog, setOpenRemovePropertyAccessDialog] = useState(false);
  const [selectRemoveBuildingAccess, setSelectRemoveBuildingAccess] = useState(null);

  /**
   * Handle global call forward toggle
   */
  const handleGlobalCallForwardToggle = async () => {
    if (isTogglingGlobal) return;

    const originalSettings = accountSettings;
    const newValue = !isInboundCallsEnabled;
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      dispatch(setSnackMessage({
        message: 'Authentication session expired. Please log in again.',
        level: 'error',
        duration: 5000
      }));
      return;
    }

    // Optimistic update
    dispatch(setAccountSettings({
      ...accountSettings,
      Twilio: { ...accountSettings?.Twilio, InboundCallsEnabled: newValue }
    }));

    setIsTogglingGlobal(true);
    try {
      if (newValue) {
        await SPBackend.enableInboundCalls(accountId, firebaseUser);
      } else {
        await SPBackend.disableInboundCalls(accountId, firebaseUser);
      }
      dispatch(setSnackMessage({
        message: newValue ? 'Call forward enabled' : 'Call forward disabled',
        level: 'success',
        duration: 3000
      }));
    } catch (error) {
      // Rollback
      dispatch(setAccountSettings(originalSettings));
      dispatch(setSnackMessage({
        message: 'Failed to update. Please try again.',
        level: 'error',
        duration: 5000
      }));
    } finally {
      setIsTogglingGlobal(false);
    }
  };

  /**
   * Handle individual staff call forward toggle
   */
  const handleStaffCallForwardToggle = (staffUser: User) => {
    const currentValue = staffUser.Settings?.ReceiveInboundCalls !== false;
    const newValue = !currentValue;

    updateStaffCallForward({ staffUser, newValue });
  };

  const selectStaff = (staff) => {
    setSelectedStaff(staff);
  };

  const handleRemoveStaff = async () => {
    if (selectedStaff !== null) {
      try {
        await removeStaffMutation({ staff: selectedStaff }).unwrap();
        dispatch(setSnackMessage({
          message: 'Staff removed.',
          level: 'success',
          duration: 3000
        }));
      } catch (error) {
        log('[handleRemoveStaff] error: ', error);
        dispatch(setSnackMessage({
          message: error.data || 'An error occurred. Please try again',
          level: 'error',
          duration: 5000
        }));
      }
      setSelectedStaff(null);
    } else {
      dispatch(setSnackMessage({
        message: 'An error occurred. Please try again.',
        level: 'error',
        duration: 3000
      }));
    }
  };

  const handleEditStaff = async (changedStaff: Object) => {
    if (selectedStaff !== null) {
      try {
        await editStaffMutation({ staff: selectedStaff, changedStaff }).unwrap();
        dispatch(setSnackMessage({
          message: 'Staff Updated',
          level: 'success',
          duration: 3000
        }));
      } catch (error) {
        log('[handleEditStaff] error: ', error);
        dispatch(setSnackMessage({
          message: error.data || 'An error occurred. Please try again',
          level: 'error',
          duration: 5000
        }));
      }
      setSelectedStaff(null);
    } else {
      dispatch(setSnackMessage({
        message: 'An error occurred. Please try again.',
        level: 'error',
        duration: 3000
      }));
    }
  };

  const handleAddPropertyAccess = async (staff: User) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      dispatch(setSnackMessage({
        message: 'Authentication session expired. Please log in again.',
        level: 'error',
        duration: 5000
      }));
      return;
    }

    log('[handleAddPropertyAccess] staff ', staff);
    try {
      await SPBackend.addUserPermission(staff, firebaseUser);
      dispatch(setSnackMessage({
        message: 'Access Granted',
        level: 'success',
        duration: 3000
      }));
      refetch();
    } catch (error) {
      log('[handleAddPropertyAccess.addUserPermission] error: ', error);
      dispatch(setSnackMessage({
        message: error.response ? error.response.data : 'An error occurred. Please try again',
        level: 'error',
        duration: 5000
      }));
    }
  };

  const handleRemovePropertyAccess = async (staff: User) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      dispatch(setSnackMessage({
        message: 'Authentication session expired. Please log in again.',
        level: 'error',
        duration: 5000
      }));
      return;
    }

    log('[handleRemovePropertyAccess] staff ', staff);
    try {
      await SPBackend.removeUserPermission(staff, firebaseUser);
      dispatch(setSnackMessage({
        message: 'Access removed',
        level: 'success',
        duration: 3000
      }));
      refetch();
    } catch (error) {
      log('[handleRemovePropertyAccess.removeUserPermission] error: ', error);
      dispatch(setSnackMessage({
        message: error.response ? error.response.data : 'An error occurred. Please try again',
        level: 'error',
        duration: 5000
      }));
    }
  };

  const onCopy = () => {
    dispatch(setSnackMessage({
      message: 'Copied to your clipboard!',
      level: 'success',
      duration: 2000
    }));
  };

  // Action handlers for ConfigurableTable
  const actionHandlers = {
    handleEditStaff: (staff) => {
      selectStaff(staff.user);
      setOpenConfirmEditDialog(true);
    },
    handleRemoveStaff: (staff) => {
      selectStaff(staff.user);
      setOpenConfirmRemoveDialog(true);
    }
  };

  // Cell renderer for each column
  const renderCell = (staff, column, index) => {
    if (!staff.user) {
      return 'Staff not found';
    }

    switch (column.key) {
      case 'name':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {`${staff.user.FirstName} ${staff.user.LastName}`}
            {typeof staff.user !== 'undefined'
            && typeof staff.user.Invite !== 'undefined'
            && staff.user.Invite.Status !== 'accepted'
            ? <Box component="span" sx={classes.inlineSpan}>
                {moment().diff(staff.user.Invite.CreatedDt, 'days', true) > 1
                  ? '(Invite expired.)'
                  : <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      {`(Invited by ${staff.user.Invite.Channel === 'email' ? 'Email' : 'SMS'})`}
                      <CopyToClipboard
                        onCopy={onCopy}
                        text={`https://app.askneo.io/staff/invitations/${staff.user.Invite.Token}`}
                      >
                        <Tooltip title='Copy Invite Link'>
                          <IconButton
                            aria-label="Copy Invite link"
                            sx={classes.inlineBtn}
                            edge="end"
                          >
                            <FileCopy />
                          </IconButton>
                        </Tooltip>
                      </CopyToClipboard>
                    </Box>
                }
              </Box>
            : null}
          </Box>
        );
      case 'email':
        return typeof staff.user.Email !== 'undefined' && staff.user.Email !== '' ? staff.user.Email : '';
      case 'phone':
        return typeof staff.user.Phone !== 'undefined' && staff.user.Phone !== '' ? formatPhoneNumberForFrontend(staff.user.Phone) : '';
      case 'callForward':
        const hasPhone = !!staff.user.Phone?.trim();
        const receivesCalls = staff.user.Settings?.ReceiveInboundCalls !== false;

        if (!hasPhone) {
          return (
            <Typography variant="body2" color="textSecondary">
              No phone
            </Typography>
          );
        }

        return (
          <Tooltip title={receivesCalls
            ? `${staff.user.FirstName} will receive forwarded calls`
            : `${staff.user.FirstName} won't receive forwarded calls`
          }>
            <Switch
              checked={receivesCalls}
              onChange={() => handleStaffCallForwardToggle(staff.user)}
              disabled={!isInboundCallsEnabled}
              size="small"
            />
          </Tooltip>
        );
      case 'role':
        return getUserRole(staff.user);
      default:
        return null;
    }
  };

  // Mobile item renderer
  const renderMobileItem = (staff, index) => {
    if (!staff.user) {
      return <Typography>{'Staff not found'}</Typography>;
    }

    const hasPhone = !!staff.user.Phone?.trim();
    const receivesCalls = staff.user.Settings?.ReceiveInboundCalls !== false;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={classes.tileLeftColCtr}>
          <Typography variant='body1' sx={classes.tileTitle}>
            {staff.user.FirstName} {staff.user.LastName}
          </Typography>
          <Typography variant='body1' sx={classes.tileSubTitle}>
            {staff.user.Email}
          </Typography>
          <Typography variant='body1' sx={classes.tileSubTitle}>
            {formatPhoneNumberForFrontend(staff.user.Phone)}
          </Typography>
          <Typography variant='body1' sx={classes.tileSubTitle}>
            {getUserRole(staff.user)}
          </Typography>
          {hasPhone && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title={receivesCalls
                ? `${staff.user.FirstName} will receive forwarded calls`
                : `${staff.user.FirstName} won't receive forwarded calls`
              }>
                <Switch
                  checked={receivesCalls}
                  onChange={() => handleStaffCallForwardToggle(staff.user)}
                  disabled={!isInboundCallsEnabled}
                  size="small"
                />
              </Tooltip>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Call Forward
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={classes.tileRightColCtr}>
          {(((ROLE_POWER[userRole] > getRolePower(staff.user)) && (email !== staff.user.Email))
            || (ROLE_POWER[userRole] === 3))
            ? <IconButton
                aria-label="Edit User"
                sx={classes.tileActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  actionHandlers.handleEditStaff(staff);
                }}
              >
                <EditIcon />
              </IconButton>
            : null
          }
          {
            ((email !== staff.user.Email)
            || (ROLE_POWER[userRole] === 3 && email !== staff.user.Email))
            ? <Tooltip
                aria-label={'Remove Staff'}
                title={'Remove Staff'}
              >
                <IconButton
                  aria-label="Remove Staff"
                  sx={classes.tileActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    actionHandlers.handleRemoveStaff(staff);
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              </Tooltip>
            : null
          }
        </Box>
      </Box>
    );
  };

  return (
    <ContentContainer
      hasSubheader={true}
      sx={classes.staffTableContent}
    >
      {/* Staff count and Call Forward Settings */}
      {!isFetching && (
        <Box sx={classes.callForwardSettings}>
          <Box sx={classes.staffCount}>
            {staffList.length} {staffList.length === 1 ? 'staff member' : 'staff members'}
          </Box>
          <Tooltip title="When enabled, incoming calls are forwarded to staff members with Call Forward active">
            <FormControlLabel
              control={
                <Switch
                  checked={isInboundCallsEnabled}
                  onChange={handleGlobalCallForwardToggle}
                  disabled={isTogglingGlobal}
                />
              }
              label={isInboundCallsEnabled ? 'Call Forward: Enabled' : 'Call Forward: Disabled'}
            />
          </Tooltip>
        </Box>
      )}

      {isFetching ? null : staffList && staffList.length > 0 ? (
        <ConfigurableTable
          data={staffList}
          config={STAFF_TABLE_CONFIG}
          renderCell={renderCell}
          renderMobileItem={renderMobileItem}
          actionHandlers={actionHandlers}
          emptyMessage="No staff found"
          totalCount={staffList.length}
        />
      ) : (
        <Box sx={classes.simpleMessage}>
          <Typography variant='h3'>{'This is your staff directory'}</Typography>
          <br />
          <Typography variant='subtitle1'>{'Add staff members here to transform your Feed into a shared inbox and:'}
            <br />{'- Collaborate and assign tickets'}
            <br />{'- Visualize conversations threads'}
            <br />{'- Improve team communication'}
          </Typography>
        </Box>
      )}

      <EditStaffDialog
        open={openConfirmEditDialog}
        setOpen={setOpenConfirmEditDialog}
        staff={selectedStaff}
        triggerAction={handleEditStaff}
      />
      <RemoveStaff
        actionConfirm={handleRemoveStaff}
        open={openConfirmRemoveDialog}
        setOpen={setOpenConfirmRemoveDialog}
        staff={selectedStaff}
      />
      <AddPropertyAccessDialog
        handleAddPropertyAccess={handleAddPropertyAccess}
        open={openAddPropertyAccessDialog}
        selectedStaff={selectedStaff}
        setOpen={setOpenAddPropertyAccessDialog}
      />
      <RemovePropertyAccessDialog
        building={selectRemoveBuildingAccess}
        handleRemovePropertyAccess={handleRemovePropertyAccess}
        open={openRemovePropertyAccessDialog}
        selectedStaff={selectedStaff}
        setOpen={setOpenRemovePropertyAccessDialog}
      />
    </ContentContainer>
  );
};

export default StaffListContent;
